import { siteConfig } from '@/config/site.config'
import { enUS, fr } from 'date-fns/locale'
import React from 'react'

// Types definition
export const SUPPORTED_LANG_CODES = ['en', 'fr'] as const
export type LangCode = typeof SUPPORTED_LANG_CODES[number]
export type I18nDictionary = { [key: string]: any }
export type I18nValue = { [lang: string]: string }

// Languages definition
export const LANGS: I18nDictionary = {
  'en': 'English',
  'fr': 'Français'
}

// CONSTANTS
export const DEFAULT_LANG_CODE: LangCode = 'fr'

export const isLangCode = (v: string): v is LangCode =>
  (SUPPORTED_LANG_CODES as readonly string[]).includes(v)

export function toLangCode(lang: string): LangCode {
  return (typeof lang === 'string' && LANGS[lang]) ? (lang as LangCode) : DEFAULT_LANG_CODE
}

export function getLanguage(langCode: LangCode, underscore: boolean=true): string {
  switch(langCode) {
    case 'fr':
      return underscore ? 'fr_FR' : 'fr-FR'
    default:
      return underscore ? 'en_US' : 'en-US'
  }
}
export function getLocale(langCode: LangCode): Locale {
  switch(langCode) {
    case 'fr':
      return fr
    default:
      return enUS
  }
}

export function getLocalizedUri(path: string, langCode: LangCode, keepDefaultLangCode: boolean = false): string {
  if(!path) {
    path = '/'
  }
  else if(!path.startsWith('/')) {
    path = '/' + path
  }
  path = !keepDefaultLangCode && langCode === DEFAULT_LANG_CODE ? path : '/' + langCode + path
  // Remove potential trailing '/'
  if(path.length > 1 && path.endsWith('/')) {
    path = path.substring(0, path.length-1)
  }
  return path
}
export function getFullLocalizedUri(path: string, langCode: LangCode, keepDefaultLangCode: boolean = false): string {
  return siteConfig.links.site + getLocalizedUri(path, langCode, keepDefaultLangCode)
}

// USE I18n
// ---------------------------------------------------------------------------------

const _getI18n = (input: I18nDictionary, langCode: LangCode): I18nDictionary => {
  if (React.isValidElement(input)) return input
  if (Array.isArray(input)) return input.map(elt => _getI18n(elt, langCode))
  if (typeof input === 'object') {
    if (typeof input[langCode] !== 'undefined') return _getI18n(input[langCode], langCode)
    const res: { [key: string]: any } = {}
    for (const [key, value] of Object.entries(input))
      res[key] = _getI18n(value, langCode)
    return res
  }
  return input
}

const _getI18nValue = (input: I18nValue, langCode: LangCode): string => {
  let value = input[langCode]
  if(!value && langCode !== 'en')
    value = input['en']
  if(!value && langCode !== 'fr')
    value = input['fr']
  return value
}

export function getI18n(I18nDictionary: I18nDictionary, langCode: LangCode) {
  return _getI18n(I18nDictionary, langCode)
}

export function useI18nValue() {
  const i18nValue = (input: I18nValue, langCode: LangCode) => _getI18nValue(input, langCode)
  return i18nValue
}
